import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';

import { TenantAdminService } from '../../admin/service/tenant-admin.service';
import { TenantSummary, CreateTenantRequest } from '../../admin/models/tenant-admin.model';
import { CreateTenantDialogComponent } from '../create-tenant-dialog/create-tenant-dialog.component';
import { ApiKeyDialogComponent } from '../api-key-dialog/api-key-dialog.component';

@Component({
  selector: 'app-manage-tenants',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatMenuModule,
    MatDialogModule,
    MatChipsModule,
    MatSnackBarModule,
    MatProgressBarModule,
    MatInputModule,
    MatFormFieldModule
  ],
  templateUrl: './manage-tenant.component.html',
  styleUrls: ['./manage-tenant.component.scss']
})
export class ManageTenantComponent {
  private tenantSvc = inject(TenantAdminService);
  private dialog = inject(MatDialog);
  private snack = inject(MatSnackBar);

  loading = false;
  filterText = '';

  displayedColumns = ['name', 'tenant_id', 'status', 'plan_type', 'created_at', 'actions'];
  tenants: TenantSummary[] = [];

  get filteredTenants(): TenantSummary[] {
    const q = this.filterText.trim().toLowerCase();
    if (!q) return this.tenants;
    return this.tenants.filter(t =>
      (t.name ?? '').toLowerCase().includes(q) ||
      (t.tenant_id ?? '').toLowerCase().includes(q) ||
      (t.plan_type ?? '').toLowerCase().includes(q)
    );
  }

  async ngOnInit() {
    await this.loadTenants();
  }

  async loadTenants() {
    this.loading = true;
    try {
      this.tenants = await this.tenantSvc.listTenants();
    } catch (e: any) {
      this.snack.open(`Failed to load tenants`, 'Close', { duration: 3000 });
    } finally {
      this.loading = false;
    }
  }

  openCreateDialog() {
    const ref = this.dialog.open(CreateTenantDialogComponent, { width: '520px' });
    ref.afterClosed().subscribe(async (payload: CreateTenantRequest | null) => {
      if (!payload) return;
      await this.createTenant(payload);
    });
  }

  async createTenant(payload: CreateTenantRequest) {
    this.loading = true;
    try {
      const res = await this.tenantSvc.createTenant(payload);
      this.snack.open(`Tenant created: ${res.tenant_id}`, 'Close', { duration: 4000 });
      await this.loadTenants();
    } catch (e: any) {
      this.snack.open(`Create tenant failed`, 'Close', { duration: 3000 });
    } finally {
      this.loading = false;
    }
  }

  async suspend(t: TenantSummary) {
    this.loading = true;
    try {
      await this.tenantSvc.suspendTenant(t.tenant_id);
      this.snack.open(`Tenant suspended`, 'Close', { duration: 2500 });
      await this.loadTenants();
    } catch {
      this.snack.open(`Suspend failed`, 'Close', { duration: 3000 });
    } finally {
      this.loading = false;
    }
  }

  async activate(t: TenantSummary) {
    this.loading = true;
    try {
      await this.tenantSvc.activateTenant(t.tenant_id);
      this.snack.open(`Tenant activated`, 'Close', { duration: 2500 });
      await this.loadTenants();
    } catch {
      this.snack.open(`Activate failed`, 'Close', { duration: 3000 });
    } finally {
      this.loading = false;
    }
  }

  async rotateApiKey(t: TenantSummary) {
    this.loading = true;
    try {
      const apiKey = await this.tenantSvc.rotateApiKey(t.tenant_id);

      const ref = this.dialog.open(ApiKeyDialogComponent,
        {
          width: '520px', data: { apiKey: apiKey }
        });

      ref.afterClosed().subscribe(async () => {
        console.log("API Key dialog closed");
      });

      console.log('Rotated API key:', apiKey);
      // this.snack.open(`API key rotated. Please copy this API key and keep it some place safe: ${apiKey}`, 'Close', { duration: 10000 });
    } catch {
      this.snack.open(`Rotate key failed`, 'Close', { duration: 3000 });
    } finally {
      this.loading = false;
    }
  }

  copy(text: string) {
    navigator.clipboard.writeText(text);
    this.snack.open(`Copied`, 'Close', { duration: 1500 });
  }

  statusLabel(t: TenantSummary) {
    return t.is_active ? 'Active' : 'Suspended';
  }

  statusClass(t: TenantSummary) {
    return t.is_active ? 'status-active' : 'status-suspended';
  }
}
